<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({ clusters: [], generatedAt: null }),
  },
  loading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});
const emit = defineEmits(['refresh']);

const sentimentDot = s => {
  if (s === 'positive') return 'bg-emerald-500';
  if (s === 'negative') return 'bg-rose-500';
  return 'bg-slate-400';
};
const sentimentLabel = s => {
  if (s === 'positive') return 'Positivo';
  if (s === 'negative') return 'Negativo';
  return 'Neutro';
};

const sizePct = size => {
  const total = props.data.clusters?.reduce((s, c) => s + (c.size || 0), 0) || 1;
  return Math.max(2, Math.round((size / total) * 100));
};
</script>

<template>
  <div class="rounded-xl border border-n-weak bg-n-background p-4">
    <div class="mb-3 flex items-center justify-between">
      <div>
        <h3 class="text-sm font-semibold text-n-slate-12">
          Clusters de Comentários (IA)
        </h3>
        <p class="mt-0.5 text-[11px] text-n-slate-11">
          Comentários agrupados por tema, com recomendação de ação.
        </p>
      </div>
      <Button
        variant="outline"
        size="sm"
        icon="i-lucide-refresh-cw"
        :is-loading="loading"
        :disabled="disabled"
        @click="emit('refresh')"
      >
        Gerar
      </Button>
    </div>

    <div v-if="disabled" class="py-6 text-center text-sm text-n-slate-11">
      Selecione um perfil para agrupar comentários.
    </div>

    <div
      v-else-if="loading && !data.clusters?.length"
      class="flex items-center justify-center py-8"
    >
      <Spinner />
    </div>

    <div
      v-else-if="!data.clusters?.length"
      class="py-6 text-center text-sm text-n-slate-11"
    >
      Clique em "Gerar" para agrupar os comentários do período.
    </div>

    <div v-else class="space-y-2">
      <div
        v-for="(c, i) in data.clusters"
        :key="`cluster-${i}`"
        class="rounded-lg border border-n-weak p-3"
      >
        <div class="mb-2 flex items-start justify-between gap-2">
          <div class="min-w-0 flex-1">
            <div class="mb-1 flex items-center gap-2">
              <span :class="sentimentDot(c.sentimentDominant)" class="size-2 rounded-full" />
              <h4 class="truncate text-sm font-semibold text-n-slate-12">
                {{ c.label }}
              </h4>
              <span class="text-[10px] text-n-slate-11">
                {{ sentimentLabel(c.sentimentDominant) }} · {{ c.size }} comentários
              </span>
            </div>
            <div class="mb-2 h-1.5 overflow-hidden rounded-full bg-n-slate-3">
              <div
                class="h-full"
                :class="{
                  'bg-emerald-500': c.sentimentDominant === 'positive',
                  'bg-rose-500': c.sentimentDominant === 'negative',
                  'bg-slate-400': !c.sentimentDominant || c.sentimentDominant === 'neutral',
                }"
                :style="{ width: `${sizePct(c.size)}%` }"
              />
            </div>
          </div>
        </div>

        <div v-if="c.recommendation" class="mb-2 rounded-md bg-blue-50 p-2 text-[11px] text-blue-900">
          <span class="font-semibold">→ </span>{{ c.recommendation }}
        </div>

        <div v-if="c.exemplars?.length" class="space-y-1">
          <div
            v-for="(ex, j) in c.exemplars"
            :key="`ex-${i}-${j}`"
            class="rounded-md bg-n-slate-2 p-2 text-[11px]"
          >
            <div class="mb-0.5 flex items-center gap-2">
              <span class="font-medium text-n-slate-12">{{ ex.authorName }}</span>
              <span class="text-[9px] text-n-slate-11">♥ {{ ex.likes }}</span>
            </div>
            <p class="text-n-slate-11">{{ ex.content }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
